import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export type AppRole = "admin" | "moderator" | "verified_user";

export const useAdminRole = () => {
  const { user } = useAuthContext();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isAdminOrModerator, setIsAdminOrModerator] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkRole = useCallback(async (role: AppRole): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .rpc("has_role", { _user_id: user.id, _role: role });

      if (error) {
        console.error("Error checking role:", error);
        return false;
      }
      return data || false;
    } catch (error) {
      console.error("Error checking role:", error);
      return false;
    }
  }, [user]);

  const checkIsAdminOrModerator = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .rpc("is_admin_or_moderator");

      if (error) {
        console.error("Error checking admin/moderator:", error);
        return false;
      }
      return data || false;
    } catch (error) {
      console.error("Error checking admin/moderator:", error);
      return false;
    }
  }, [user]);

  useEffect(() => {
    const fetchRoles = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsModerator(false);
        setIsAdminOrModerator(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      const [adminResult, moderatorResult, adminOrModResult] = await Promise.all([
        checkRole("admin"),
        checkRole("moderator"),
        checkIsAdminOrModerator(),
      ]);

      setIsAdmin(adminResult);
      setIsModerator(moderatorResult);
      setIsAdminOrModerator(adminOrModResult);
      setLoading(false);
    };

    fetchRoles();
  }, [user, checkRole, checkIsAdminOrModerator]);

  return {
    isAdmin,
    isModerator,
    isAdminOrModerator,
    loading,
    checkRole,
    checkIsAdminOrModerator,
  };
};
